import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Calculate as CalculatorIcon,
  AttachMoney as MoneyIcon,
  Home as HomeIcon,
  Save as SaveIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ResourceContactForm from '../../components/forms/ResourceContactForm';

interface CalculationResults {
  purchasePrice: number;
  loanAmount: number;
  downPayment: number;
  titleInsurance: number;
  attorneyFees: number;
  homeInspection: number;
  appraisal: number;
  recordingFees: number;
  transferTax: number;
  prepaidTaxes: number;
  prepaidInsurance: number;
  totalClosingCosts: number;
  totalCashNeeded: number;
}

const ClosingCostCalculator: React.FC = () => {
  const [purchasePrice, setPurchasePrice] = useState<string>('');
  const [downPaymentPercent, setDownPaymentPercent] = useState<string>('20');
  const [county, setCounty] = useState<string>('middlesex');
  const [propertyType, setPropertyType] = useState<string>('single-family');
  const [results, setResults] = useState<CalculationResults | null>(null);

  const countyData = {
    middlesex: { transferTaxRate: 0.005, recordingFee: 75 },
    bergen: { transferTaxRate: 0.005, recordingFee: 80 },
    essex: { transferTaxRate: 0.005, recordingFee: 78 },
    hudson: { transferTaxRate: 0.005, recordingFee: 85 },
    union: { transferTaxRate: 0.005, recordingFee: 82 },
  };

  const propertyTypeMultipliers = {
    'single-family': 1.0,
    'condo': 1.1,
    'townhouse': 1.05,
    'multi-family': 1.15,
  };

  const calculateClosingCosts = () => {
    const price = parseFloat(purchasePrice);
    const downPercent = parseFloat(downPaymentPercent) / 100;
    
    if (!price || price <= 0) return;

    const downPayment = price * downPercent;
    const loanAmount = price - downPayment;
    
    const selectedCounty = countyData[county as keyof typeof countyData];
    const propertyMultiplier = propertyTypeMultipliers[propertyType as keyof typeof propertyTypeMultipliers];

    // Calculate each cost component
    const titleInsurance = (price * 0.0035) * propertyMultiplier; // Approximate NJ title insurance rate
    const attorneyFees = propertyType === 'condo' ? 1200 : 1000;
    const homeInspection = propertyType === 'single-family' ? 500 : 350;
    const appraisal = 500;
    const recordingFees = selectedCounty.recordingFee;
    const transferTax = price * selectedCounty.transferTaxRate;
    const prepaidTaxes = (price * 0.025) / 4; // Quarterly property taxes
    const prepaidInsurance = 1200; // Annual homeowner's insurance

    const totalClosingCosts = titleInsurance + attorneyFees + homeInspection + 
                             appraisal + recordingFees + transferTax + 
                             prepaidTaxes + prepaidInsurance;
    
    const totalCashNeeded = downPayment + totalClosingCosts;

    setResults({
      purchasePrice: price,
      loanAmount,
      downPayment,
      titleInsurance,
      attorneyFees,
      homeInspection,
      appraisal,
      recordingFees,
      transferTax,
      prepaidTaxes,
      prepaidInsurance,
      totalClosingCosts,
      totalCashNeeded,
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <>
      <Helmet>
        <title>NJ Closing Cost Calculator | Free Tool | MAD Title Agency</title>
        <meta
          name="description"
          content="Free closing cost calculator for New Jersey home purchases. Get accurate estimates for title insurance, attorney fees, and total closing costs."
        />
        <meta
          name="keywords"
          content="closing cost calculator, New Jersey closing costs, home buying costs, title insurance calculator, NJ real estate costs"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            Closing Cost Calculator
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#1e3a8a', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                NJ Closing Cost Calculator
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                Get an accurate estimate of your closing costs for New Jersey property purchases
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <CalculatorIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Free Calculator
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Instant Results
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          {/* Calculator Form */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4 }}>
              <Typography variant="h4" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
                Calculate Your Closing Costs
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Purchase Price"
                    value={purchasePrice}
                    onChange={(e) => setPurchasePrice(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    placeholder="500,000"
                    type="number"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Down Payment %"
                    value={downPaymentPercent}
                    onChange={(e) => setDownPaymentPercent(e.target.value)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    type="number"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Property Type</InputLabel>
                    <Select
                      value={propertyType}
                      label="Property Type"
                      onChange={(e) => setPropertyType(e.target.value)}
                    >
                      <MenuItem value="single-family">Single Family Home</MenuItem>
                      <MenuItem value="condo">Condominium</MenuItem>
                      <MenuItem value="townhouse">Townhouse</MenuItem>
                      <MenuItem value="multi-family">Multi-Family</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>County</InputLabel>
                    <Select
                      value={county}
                      label="County"
                      onChange={(e) => setCounty(e.target.value)}
                    >
                      <MenuItem value="middlesex">Middlesex County</MenuItem>
                      <MenuItem value="bergen">Bergen County</MenuItem>
                      <MenuItem value="essex">Essex County</MenuItem>
                      <MenuItem value="hudson">Hudson County</MenuItem>
                      <MenuItem value="union">Union County</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={calculateClosingCosts}
                    sx={{
                      background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                      py: 2,
                      fontSize: '1.1rem',
                    }}
                    startIcon={<CalculatorIcon />}
                  >
                    Calculate Closing Costs
                  </Button>
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  This calculator provides estimates based on typical New Jersey closing costs. 
                  Actual costs may vary based on your specific transaction details.
                </Typography>
              </Alert>
            </Card>
          </Grid>

          {/* Results */}
          <Grid item xs={12} md={6}>
            {results ? (
              <Card sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1e3a8a', flexGrow: 1 }}>
                    Your Closing Cost Estimate
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PrintIcon />}
                    sx={{ ml: 2 }}
                  >
                    Print
                  </Button>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Purchase Price
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {formatCurrency(results.purchasePrice)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Down Payment ({downPaymentPercent}%)
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {formatCurrency(results.downPayment)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Closing Cost Breakdown
                </Typography>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Title Insurance</TableCell>
                        <TableCell align="right">{formatCurrency(results.titleInsurance)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Attorney Fees</TableCell>
                        <TableCell align="right">{formatCurrency(results.attorneyFees)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Home Inspection</TableCell>
                        <TableCell align="right">{formatCurrency(results.homeInspection)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Appraisal</TableCell>
                        <TableCell align="right">{formatCurrency(results.appraisal)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Recording Fees</TableCell>
                        <TableCell align="right">{formatCurrency(results.recordingFees)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Transfer Tax</TableCell>
                        <TableCell align="right">{formatCurrency(results.transferTax)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Prepaid Property Taxes</TableCell>
                        <TableCell align="right">{formatCurrency(results.prepaidTaxes)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Prepaid Insurance</TableCell>
                        <TableCell align="right">{formatCurrency(results.prepaidInsurance)}</TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Total Closing Costs</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(results.totalClosingCosts)}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#1e3a8a' }}>
                        <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                          Total Cash Needed at Closing
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: 'white' }}>
                          {formatCurrency(results.totalCashNeeded)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Note:</strong> This estimate includes typical closing costs for {county.charAt(0).toUpperCase() + county.slice(1)} County. 
                    Additional costs may apply based on your specific loan terms and property details.
                  </Typography>
                </Box>
              </Card>
            ) : (
              <Card sx={{ p: 4, textAlign: 'center' }}>
                <HomeIcon sx={{ fontSize: 80, color: '#e2e8f0', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  Enter your home details to calculate closing costs
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Our calculator will provide a detailed breakdown of all expected closing costs for your New Jersey home purchase.
                </Typography>
              </Card>
            )}
          </Grid>
        </Grid>

        {/* Information Section */}
        <Box sx={{ mt: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a', textAlign: 'center' }}>
            Understanding Your Closing Costs
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <MoneyIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Title Insurance
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Protects you and your lender from potential title defects, liens, or ownership disputes. 
                  Required by most lenders and highly recommended for all buyers.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <HomeIcon sx={{ fontSize: 48, color: '#10b981', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Attorney Fees
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  In New Jersey, attorneys typically handle real estate closings. 
                  Fees vary based on property type and transaction complexity.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <SaveIcon sx={{ fontSize: 48, color: '#8b5cf6', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Prepaid Costs
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Property taxes, homeowner's insurance, and mortgage interest 
                  that must be paid in advance at closing.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Contact Form Section */}
        <ResourceContactForm
          resourceTitle="Closing Cost Calculator"
          resourceType="consumer"
          formType="quote"
        />
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
              Ready to Start Your Home Purchase?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
              Get personalized assistance with your title insurance and closing needs.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Get a Quote
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/resources"
                sx={{
                  borderColor: '#1e3a8a',
                  color: '#1e3a8a',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                More Tools & Guides
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default ClosingCostCalculator; 